<template>
  <v-card flat>
    <b-row>
      <b-col :md="4">
        <b-form-group
          id="item-gas-stations-group"
          v-bind:label="$t('FORM_LABELS.VENDOR')"
          label-for="item-assignor-input"
        >
          <FormSelect
            id="item-gas-stations-input"
            type="select"
            clearable
            :options="serviceVendors"
            v-model="form.serviceVendorId"
            :validateState="validateState('serviceVendorId')"
          ></FormSelect>
        </b-form-group>
      </b-col>
      <b-col :md="4">
        <b-form-group
          id="item-bill-number-group"
          :label="$t('FORM_LABELS.INVOICE_NUMBER')"
          label-for="item-bill-number-input"
        >
          <div class="input-group">
            <FormInput
              id="bill-invoice-number-input"
              type="text"
              name="bill_number"
              :i18n="{
                placeholder: $t('FORM_LABELS.INVOICE_NUMBER')
              }"
              v-model="form.invoiceNumber"
              :validateState="validateState('invoiceNumber')"
            ></FormInput>
          </div>
        </b-form-group>
      </b-col>
      <b-col :md="4">
        <b-form-group
          id="item-bill-number-group"
          :label="$t('FORM_LABELS.DATE')"
          label-for="item-bill-number-input"
        >
          <FormDatepicker
            id="invoice-date-input"
            type="text"
            v-model="form.invoiceDate"
            :validateState="validateState('invoiceDate')"
          ></FormDatepicker>
        </b-form-group>
      </b-col>
    </b-row>

    <div class="form-group mt-5">
      <label>
        <input type="checkbox" v-model="form.toAccounting" />
        {{ $t("FORM_LABELS.TO_ACCOUNTING") }}
      </label>
    </div>

    <ServiceInvoiceFormDefaultSettings
      :defaultMachine.sync="defaultMachine"
      :defaultWarehouseItem.sync="defaultWarehouseItem"
      :machines="machines"
      :serviceWarehouseItems="serviceWarehouseItems"
      @apply-to-all="applyDefaultsToAllRows"
    />

    <b-row v-if="form.rows.length > 0">
      <b-col :md="2"><div></div></b-col>
      <b-col :md="4">
        <div class="text-right">
          <p class="pt-8">
            <b>{{ $t("FORM_LABELS.INVOICE_VALUE") }}:</b>
          </p>
        </div>
      </b-col>
      <b-col :md="2">
        <b-form-group
          id="item-bill-measure-group"
          :label="$t('FORM_LABELS.VALUE_NO_VAT')"
          label-for="item-bill-total-value"
        >
          <div class="input-group">
            <FormInput
              id="item-bill-total-value"
              type="text"
              :i18n="{
                placeholder: $t('FORM_LABELS.VALUE_NO_VAT')
              }"
              v-model="form.totalNoVat"
              readonly
            ></FormInput>
          </div>
        </b-form-group>
      </b-col>
      <b-col :md="2">
        <b-form-group
          id="item-bill-measure-group"
          :label="$t('FORM_LABELS.VALUE_VAT')"
          label-for="item-bill-qty-input"
        >
          <div class="input-group">
            <FormInput
              id="item-bill-qty-input"
              type="text"
              :i18n="{
                placeholder: $t('FORM_LABELS.VALUE_VAT')
              }"
              v-model="form.vat"
              readonly
            ></FormInput>
          </div>
        </b-form-group>
      </b-col>
      <b-col :md="2">
        <b-form-group
          id="item-bill-measure-group"
          :label="$t('FORM_LABELS.VALUE_WITH_VAT')"
          label-for="item-bill-qty-input"
        >
          <div class="input-group">
            <FormInput
              id="item-bill-qty-input-with-vat"
              type="text"
              :i18n="{
                placeholder: $t('FORM_LABELS.VALUE_WITH_VAT')
              }"
              v-model="form.totalWithVat"
              readonly
            ></FormInput>
          </div>
        </b-form-group>
      </b-col>
    </b-row>
    <ServiceInvoicesRowsTable
      :rows="form.rows"
      :machines="machines"
      :serviceWarehouseItems="serviceWarehouseItems"
      :rowsWithErrors="rowsWithErrors"
      :rowValidationErrors="rowValidationErrors"
      @add-row="addNewRow"
      @update-row="updateRowField"
      @delete-row="deleteRow"
    ></ServiceInvoicesRowsTable>
  </v-card>
</template>

<script>
import { FETCH_SERVICE_VENDORS } from "@/modules/service-vendors/store/service-vendors.module";
import { FETCH_MACHINES } from "@/modules/machines/store/machine.module";
import { FETCH_SERVICE_WAREHOUSE_ITEMS } from "@/modules/service-warehouse-items/store/service-warehouse-items.module";

import FormInput from "@/view/content/forms/components/FormInput.vue";
import FormDatepicker from "@/view/content/forms/components/FormDatepicker.vue";
import FormSelect from "@/view/content/forms/components/FormSelect";
import ServiceInvoiceFormDefaultSettings from "./ServiceInvoiceFormDefaultSettings.vue";
import ServiceInvoicesRowsTable from "./ServiceInvoicesRowsTable";

import { validationMixin } from "vuelidate";
import { formBuilderMixin } from "@/core/mixins/formBuilderMixin.js";

export default {
  mixins: [validationMixin, formBuilderMixin],
  components: {
    FormSelect,
    FormInput,
    FormDatepicker,
    ServiceInvoiceFormDefaultSettings,
    ServiceInvoicesRowsTable
  },
  props: {
    form: Object
  },
  validations() {
    const tmpValidationObject = {
      form: {
        serviceVendorId: this.setItemValidations({
          required: true
        }),
        invoiceNumber: this.setItemValidations({
          required: true
        }),
        invoiceDate: this.setItemValidations({
          required: true,
          minLength: 2
        })
      }
    };

    return tmpValidationObject;
  },
  data() {
    return {
      serviceVendors: [],
      machines: [],
      serviceWarehouseItems: [],
      rowsWithErrors: [],
      rowValidationErrors: {},
      newRowCounter: 0,
      defaultMachine: null,
      defaultWarehouseItem: null
    };
  },
  watch: {
    "form.rows": {
      handler() {
        this.recalculateTotals();
      },
      deep: true
    },
    defaultMachine(newValue) {
      if (newValue) {
        this.applyDefaultMachineToAllRows(newValue);
      }
    },
    defaultWarehouseItem(newValue) {
      if (newValue) {
        this.applyDefaultWarehouseToAllRows(newValue);
      }
    }
  },
  mounted() {
    this.loadInitialData();
  },
  methods: {
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },
    async loadInitialData() {
      await Promise.all([
        this.$store.dispatch(FETCH_SERVICE_VENDORS).then(data => {
          this.serviceVendors = data.data.items;
        }),
        this.$store
          .dispatch(FETCH_MACHINES, this.$url.transformParams({ active: 1 }))
          .then(data => {
            this.machines = data.data.items.map(v => {
              return {
                id: {
                  id: v.id,
                  type: v.type,
                  lastUsedStaffId: v.lastUsedStaffId || null
                },
                name: v.name
              };
            });
          }),
        this.$store.dispatch(FETCH_SERVICE_WAREHOUSE_ITEMS).then(data => {
          this.serviceWarehouseItems = data.data.items.map(i => {
            return {
              id: i.id,
              name: i.name
            };
          });
        })
      ]);
    },
    applyDefaultMachineToAllRows(machine) {
      if (!this.form.rows || this.form.rows.length === 0) return;

      this.form.rows.forEach(row => {
        row.machine = machine;
        row.machineId = machine.id.id;
      });

      this.form.rows.forEach((row, index) => {
        if (this.rowValidationErrors[index]) {
          this.$delete(this.rowValidationErrors[index], "machine");
          if (row.machineId || row.serviceWarehouseItemId) {
            this.$delete(
              this.rowValidationErrors[index],
              "serviceWarehouseItem"
            );
          }
          if (Object.keys(this.rowValidationErrors[index]).length === 0) {
            this.$delete(this.rowValidationErrors, index);
            const errorIdx = this.rowsWithErrors.indexOf(index);
            if (errorIdx > -1) {
              this.rowsWithErrors.splice(errorIdx, 1);
            }
          }
        }
      });
    },
    applyDefaultWarehouseToAllRows(warehouseItemId) {
      if (!this.form.rows || this.form.rows.length === 0) return;

      this.form.rows.forEach(row => {
        row.serviceWarehouseItemId = warehouseItemId;
      });

      this.form.rows.forEach((row, index) => {
        if (this.rowValidationErrors[index]) {
          this.$delete(this.rowValidationErrors[index], "serviceWarehouseItem");
          if (row.machineId || row.serviceWarehouseItemId) {
            this.$delete(this.rowValidationErrors[index], "machine");
          }
          if (Object.keys(this.rowValidationErrors[index]).length === 0) {
            this.$delete(this.rowValidationErrors, index);
            const errorIdx = this.rowsWithErrors.indexOf(index);
            if (errorIdx > -1) {
              this.rowsWithErrors.splice(errorIdx, 1);
            }
          }
        }
      });
    },
    applyDefaultsToAllRows() {
      if (this.defaultMachine) {
        this.applyDefaultMachineToAllRows(this.defaultMachine);
      }
      if (this.defaultWarehouseItem) {
        this.applyDefaultWarehouseToAllRows(this.defaultWarehouseItem);
      }
    },
    addNewRow() {
      if (!this.form.rows) {
        this.$set(this.form, "rows", []);
      }

      let machine = null;
      let machineId = null;
      let serviceWarehouseItemId = null;

      if (this.defaultMachine) {
        machine = this.defaultMachine;
        machineId = this.defaultMachine.id.id;
      }

      if (this.defaultWarehouseItem) {
        serviceWarehouseItemId = this.defaultWarehouseItem;
      }

      this.form.rows.push({
        id: null,
        tempId: `temp-row-${this.newRowCounter++}`,
        name: "",
        code: "",
        machine: machine,
        machineId: machineId,
        serviceWarehouseItemId: serviceWarehouseItemId,
        totalQty: 0,
        price: 0,
        totalPrice: 0
      });
    },
    deleteRow(index) {
      const row = this.form.rows[index];

      if (row.id) {
        this.$emit("track-deleted-row", row.id);
      }

      this.form.rows.splice(index, 1);

      const newValidationErrors = {};
      Object.keys(this.rowValidationErrors).forEach(key => {
        const idx = parseInt(key);
        if (idx < index) {
          newValidationErrors[idx] = this.rowValidationErrors[idx];
        } else if (idx > index) {
          newValidationErrors[idx - 1] = this.rowValidationErrors[idx];
        }
      });
      this.$set(this, "rowValidationErrors", newValidationErrors);

      // Rebuild errors array
      this.rowsWithErrors = this.rowsWithErrors
        .filter(idx => idx !== index)
        .map(idx => (idx > index ? idx - 1 : idx));
    },
    updateRowField(index, field, value) {
      const row = this.form.rows[index];

      if (field === "machine" && value) {
        row.machine = value;
        row.machineId = value.id.id;
      } else {
        row[field] = value;
      }

      if (field === "price" || field === "totalQty") {
        this.recalculateRowTotal(row);
      }

      if (this.rowValidationErrors[index]) {
        if (field === "name" || field === "price" || field === "totalQty") {
          this.$delete(this.rowValidationErrors[index], field);
        }

        if (field === "machine" || field === "serviceWarehouseItemId") {
          if (row.machineId || row.serviceWarehouseItemId) {
            this.$delete(this.rowValidationErrors[index], "machine");
            this.$delete(
              this.rowValidationErrors[index],
              "serviceWarehouseItem"
            );
          }
        }

        if (Object.keys(this.rowValidationErrors[index]).length === 0) {
          this.$delete(this.rowValidationErrors, index);
          const errorIdx = this.rowsWithErrors.indexOf(index);
          if (errorIdx > -1) {
            this.rowsWithErrors.splice(errorIdx, 1);
          }
        }
      }
    },
    recalculateRowTotal(row) {
      const qty = parseFloat(row.totalQty) || 0;
      const price = parseFloat(row.price) || 0;
      row.totalPrice = (qty * price).toFixed(2);
    },
    recalculateTotals() {
      if (!this.form.rows || this.form.rows.length === 0) {
        this.form.totalNoVat = "0.00";
        this.form.vat = "0.00";
        this.form.totalWithVat = "0.00";
        return;
      }

      const totalNoVat = this.form.rows.reduce(
        (sum, row) => sum + (parseFloat(row.totalPrice) || 0),
        0
      );

      this.form.totalNoVat = totalNoVat.toFixed(2);

      const vatValue = totalNoVat * 0.2;
      this.form.vat = vatValue.toFixed(2);

      const totalWithVat = totalNoVat + vatValue;
      this.form.totalWithVat = totalWithVat.toFixed(2);
    },
    validateRows() {
      const errors = [];
      const validationErrors = {};

      this.form.rows.forEach((row, index) => {
        const rowErrors = {};

        if (!row.name || row.name.trim() === "") {
          rowErrors.name = true;
          if (!errors.includes(index)) {
            errors.push(index);
          }
        }

        if (!row.price || parseFloat(row.price) <= 0) {
          rowErrors.price = true;
          if (!errors.includes(index)) {
            errors.push(index);
          }
        }

        if (!row.totalQty || parseFloat(row.totalQty) <= 0) {
          rowErrors.totalQty = true;
          if (!errors.includes(index)) {
            errors.push(index);
          }
        }

        if (!row.machineId && !row.serviceWarehouseItemId) {
          rowErrors.machine = true;
          rowErrors.serviceWarehouseItem = true;
          if (!errors.includes(index)) {
            errors.push(index);
          }
        }

        if (Object.keys(rowErrors).length > 0) {
          validationErrors[index] = rowErrors;
        }
      });

      this.$set(this, "rowsWithErrors", errors);
      this.$set(this, "rowValidationErrors", validationErrors);
      return this.rowsWithErrors.length === 0;
    }
  }
};
</script>

<style type="text/css">
.input-lg input {
  font-size: 14px;
  font-weight: bold;
}
.vs__dropdown-toggle {
  background-color: #fff;
}
#item-bol-list-group label {
  font-weight: bold;
  font-size: 15px;
}
.is-invalid,
.is-invalid >>> .v-input__control,
.is-invalid >>> .v-select__selection,
.is-invalid >>> input,
.is-invalid >>> .vs__dropdown-toggle {
  border-color: #f64e60 !important;
  background-color: #ffe2e5 !important;
}

.is-invalid >>> .v-text-field__slot input {
  color: #f64e60;
}
</style>
